import { useDropzone } from 'react-dropzone';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux-hooks';
import { FC, useCallback, useState } from 'react';
import { TFilesData } from '@answerr/types';
import QuizService from '../../../services/quiz.service';
import { setToastInfo } from '../../../store/common.slice';
import { ToastType } from '../../../types/common.types';
import { inputFieldStyles } from '../../../global-styles';

interface UploadQuizFilesProps {
  onClose: () => void;
  filesData?: TFilesData[];
  quizId?: string;
  extractImages?: boolean;
}

const UploadQuizFiles: FC<UploadQuizFilesProps> = ({
  onClose,
  filesData,
  quizId,
  extractImages,
}) => {
  const dispatch = useAppDispatch();
  const { orgInfo } = useAppSelector((state) => state.common);
  const [files, setFiles] = useState<File[]>([]);
  const fileLimit = orgInfo?.fileLimit ? orgInfo.fileLimit : 10;

  const uploadDataToPinecone = useCallback(async (acceptedFiles: File[]) => {
    try {
      const formData = new FormData();
      formData.append('orgId', orgInfo?._id ?? '');
      formData.append('quizId', quizId ?? '');
      acceptedFiles.forEach((file) => {
        formData.append(`filesData`, file);
      });
      formData.append('extractImages', extractImages === true ? 'true' : 'false');
      const response = await QuizService.uploadFilesToPinecone(formData);
      if (!response.data.error) {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.SUCCESS,
            message: 'Data uploaded successfully.',
          })
        );
        onClose();
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.ERROR,
            message: response.data.message,
          })
        );
        onClose();
      }
    } catch (err) {
      dispatch(
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: 'Error in uploading data.',
        })
      );
    }
  }, [orgInfo?._id, quizId, extractImages, dispatch, onClose]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(acceptedFiles);
    uploadDataToPinecone(acceptedFiles);
  }, [uploadDataToPinecone]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: fileLimit - (Number(filesData?.length) + files.length),
    onDropRejected: (fileRejections) => {
      const error = fileRejections[0]?.errors[0];
      if (error?.code === 'file-too-large') {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.ERROR,
            message: `File cannot be more than ${
              Number(orgInfo?.fileSize) || 1
            } MB`,
          })
        );
      } else if (error?.code === 'file-invalid-type') {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.ERROR,
            message:
              'File type not supported. Supported files are: C, Cpp, Cs, Css, Doc, Docx, Go, Html, Java, Js, Json, Md, Pdf, Php, Pptx, Py, Rb, Sh, Tex, Ts, Txt',
          })
        );
      } else if (error?.code === 'too-many-files') {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.ERROR,
            message: `File limit exceeds, ${
              orgInfo?.fileLimit || 10
            } files can be uploaded per quiz.`,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            isOpen: true,
            type: ToastType.ERROR,
            message: 'File upload failed. Please try again.',
          })
        );
      }
    },
  });

  return (
    <div {...getRootProps()}>
      {quizId && <input {...getInputProps()} />}
      <div
        className={`${inputFieldStyles} items-center flex ${
          quizId ? 'cursor-pointer' : 'pointer-events-none cursor-default'
        }`}
      >
        <i className="icon-plus text-xs" />
        <span className="ml-2">Add Local Files</span>
      </div>
    </div>
  );
};

export default UploadQuizFiles;
